import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCardsOnSale } from '@sorare/core/src/hooks/search/useCardsOnSale';

import { CollectionUpsellCard } from 'components/card/CollectionUpsellCard';
import { useCart } from 'hooks/useCart';

import type {
  MissingCardsForCollectionQuery,
  MissingCardsForCollectionQueryVariables,
  MissingCardsForCollection_userCardCollection,
} from './__generated__/index.graphql';
import { useSortPrimaryBuys } from './useSortPrimaryBuys';

const Root = styled.div`
  position: relative;
  isolation: isolate;

  &::before {
    content: '';
    z-index: 1;
    position: absolute;
    left: 0;
    top: 0;
    width: var(--double-unit);
    height: 100%;
    background: linear-gradient(to right, var(--c-black) 0%, transparent 100%);
  }
  &::after {
    content: '';
    z-index: 1;
    position: absolute;
    right: 0;
    top: 0;
    width: var(--double-unit);
    height: 100%;
    background: linear-gradient(
      to left,
      var(--background-color) 0%,
      var(--background-color) 100%,
      transparent 100%
    );
    background: linear-gradient(to left, var(--c-black) 0%, transparent 100%);
  }
`;
const CollectionUpsellCards = styled(Horizontal)`
  overflow-x: auto;
  padding: var(--double-unit);
`;

const MISSING_CARDS_FOR_COLLECTION_QUERY = gql`
  query MissingCardsForCollectionQuery($slugs: [String!]) {
    anyCards(slugs: $slugs) {
      slug
      ...CollectionUpsellCard_anyCard
      ...useSortPrimaryBuys_anyCard
    }
  }
  ${CollectionUpsellCard.fragments.anyCard}
  ${useSortPrimaryBuys.fragments.anyCard}
` as TypedDocumentNode<
  MissingCardsForCollectionQuery,
  MissingCardsForCollectionQueryVariables
>;

const OPTIONAL_FILTERS = ['sale.type:PrimaryOffer'];

type Props = {
  userCardCollection: MissingCardsForCollection_userCardCollection;
};

export const MissingCardsForCollection = ({ userCardCollection }: Props) => {
  const { cart } = useCart();
  const alreadyOwnedPlayerSlugs =
    (userCardCollection?.cardCollectionCards || [])?.map(
      c => c.anyCard.anyPlayer.slug
    ) || [];

  const alreadyInCartPlayerSlugs =
    cart?.mainCartItems
      ?.filter(
        ci => ci.card.rarityTyped === userCardCollection?.cardCollection.rarity
      )
      .map(i => i.card.anyPlayer.slug) || [];

  const excludedPlayerSlugs = [
    ...alreadyOwnedPlayerSlugs,
    ...alreadyInCartPlayerSlugs,
  ];
  const { loading, slugs } = useCardsOnSale({
    index: 'Cards On Sale Lowest Price',
    distinct: true,
    rarities: [userCardCollection.cardCollection.rarity || Rarity.limited],
    teamSlug: userCardCollection.cardCollection.anyTeam?.slug,
    season: userCardCollection.cardCollection.season?.startYear,
    optionalFilters: OPTIONAL_FILTERS,
    primaryOffer: true,
    ...(excludedPlayerSlugs?.length && { excludedPlayerSlugs }),
  });

  const { data, loading: gqlLoading } = useQuery(
    MISSING_CARDS_FOR_COLLECTION_QUERY,
    {
      variables: { slugs },
      skip: !slugs.length,
    }
  );

  const sortedCardsByPrice = useSortPrimaryBuys({
    anyCards: data?.anyCards || [],
  });

  if (loading || gqlLoading || !data?.anyCards) return null;

  return (
    <Root>
      <CollectionUpsellCards gap={2}>
        {sortedCardsByPrice.map(anyCard => {
          return <CollectionUpsellCard key={anyCard.slug} anyCard={anyCard} />;
        })}
      </CollectionUpsellCards>
    </Root>
  );
};

MissingCardsForCollection.fragments = {
  userCardCollection: gql`
    fragment MissingCardsForCollection_userCardCollection on UserCardCollection {
      id
      cardCollection {
        slug
        anyTeam {
          slug
        }
        rarity
        season {
          startYear
        }
      }
      cardCollectionCards {
        id
        anyCard {
          slug
          anyPlayer {
            slug
          }
        }
      }
    }
  ` as TypedDocumentNode<MissingCardsForCollection_userCardCollection>,
};
